import { FC, ReactNode, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { LucidePencil } from 'lucide-react';
import { Order, Cart, trpc } from 'src/trpc';
import { PricingTypeEnum } from '@libs/schemas/src/pricing-rule';
import { formatDateTime } from '@libs/frontend-services';
import { useOrderAddOnProvenance } from 'components/orders/Details/orderAddOnProvenance';
import { ADDED_AFTER_BOOKING_TEXT } from './addedAfterBooking';

const PRICING_MODEL_LABELS: Record<string, string> = {
  [PricingTypeEnum.FIXED]: 'Single Stream',
  [PricingTypeEnum.PAY_AS_YOU_GO]: 'Pay As You Go',
  [PricingTypeEnum.BUNDLE]: 'Flat Rate',
  [PricingTypeEnum.STANDARD]: 'Pay As You Go',
};

interface Props {
  order: Order;
  cart: Cart;
  allMaterials?: { _id?: string; name: string }[];
  onEdit?: () => void;
}

/**
 * One fact in the Service configuration description list. Facts are separated by a rule that
 * runs down the columns on desktop and across the rows once they stack, so the three facts
 * read as equals at every width. Values wrap rather than truncate - a long dumpster or
 * material name must stay legible to dispatch.
 */
const Fact: FC<{ term: string; children: ReactNode }> = ({ term, children }) => (
  <div
    className="min-w-0 py-3 border-t border-disabled first:border-t-0 first:pt-0 last:pb-0
               sm:py-0 sm:px-5 sm:border-t-0 sm:border-l sm:first:border-l-0 sm:first:pl-0 sm:last:pr-0"
  >
    <dt className="mb-1 text-xs font-semibold text-muted">{term}</dt>
    <dd className="m-0 text-sm font-semibold text-text-primary break-words">{children}</dd>
  </div>
);

/**
 * Price-free operational summary shown first under Order Details. Two framed sections give
 * dispatch and drivers the service configuration (model, dumpster, material) and the add-ons
 * split by when they were added, without exposing any dollar amounts - every rate and amount
 * lives in the Pricing & Payment Breakdown disclosure below. Driver assignment and the
 * requested service period are shown elsewhere on the order page, so they are intentionally
 * not repeated here.
 */
export const JobSummary: FC<Props> = ({ order, cart, allMaterials, onEdit }) => {
  // Both groups read off the live order, so an item an admin removed drops out of the summary
  // just as it drops off the priced fee list in the section below.
  const { atBooking, addedAfterBooking } = useOrderAddOnProvenance(order, cart);

  const actorIds = useMemo(
    () =>
      Array.from(
        new Set(addedAfterBooking.map((a) => a.addedByUserId).filter((id): id is string => !!id)),
      ),
    [addedAfterBooking],
  );

  const { data: actors = [] } = trpc.admin.user.getUsersByIds.useQuery(actorIds, {
    enabled: actorIds.length > 0,
  });

  const actorNameMap = useMemo(() => {
    const map = new Map<string, string>();
    actors.forEach((user) => {
      if (user && user._id) {
        const fullName = `${user.name?.first || ''} ${user.name?.last || ''}`.trim();
        map.set(user._id.toString(), fullName || user.email || 'Unknown User');
      }
    });
    return map;
  }, [actors]);

  // A missing or unrecognized pricing model is treated as pay-as-you-go, matching how
  // OrderPricingSection and ChargeSummary resolve an absent model (`!pricingModel` ->
  // PAY_AS_YOU_GO). Legacy orders that predate the model stamp must show the same plan name
  // here as they do in the pricing disclosure below.
  const serviceModel =
    PRICING_MODEL_LABELS[order.quotedPricingDetails?.pricingModel || ''] ||
    PRICING_MODEL_LABELS[PricingTypeEnum.PAY_AS_YOU_GO];

  const containers = cart?.lineItems?.containers || [];

  const materialNames = (cart?.materials || [])
    .map((m) => (allMaterials || []).find((am) => am._id === m.id)?.name)
    .filter((name): name is string => !!name);

  // One shared count across both provenance groups: staff care how many items the job has
  // before they care when each was added. Add-ons and products share the list, so the count
  // and the empty states name both.
  const addOnCount = atBooking.length + addedAfterBooking.length;

  // addedAt reaches the client as an ISO string; formatDateTime accepts either form.
  const formatActorLine = (addedAt?: Date | string, addedByUserId?: string) => {
    const parts: string[] = [];
    if (addedAt) parts.push(`Added ${formatDateTime(addedAt, 'MMM d')}`);
    const name = addedByUserId ? actorNameMap.get(addedByUserId) : undefined;
    if (name) parts.push(`by ${name}`);
    return parts.join(' ');
  };

  return (
    <div className="card relative min-w-0 w-full flex flex-col items-stretch gap-4">
      {onEdit && (
        <Button
          size="icon"
          className="absolute top-0 right-0 h-11 w-11 hover:text-primary"
          variant="ghost"
          aria-label="Manage add-ons and products"
          title="Manage add-ons and products"
          onClick={onEdit}
        >
          <LucidePencil className="w-4 h-4" />
        </Button>
      )}

      {/* 48px kept clear for the pencil, the way the customer card does it. */}
      <div className="flex flex-col gap-1 pr-12">
        <h4 className="font-semibold text-base text-text-primary">Job Summary</h4>
        <p className="text-xs text-muted">
          Service, dumpster, and add-ons for dispatch verification
        </p>
      </div>

      <section
        aria-labelledby="job-summary-service-configuration"
        className="w-full rounded-lg border border-disabled bg-slate-50 p-4"
      >
        <h5
          id="job-summary-service-configuration"
          className="mb-4 text-[13px] font-semibold text-text-light"
        >
          Service configuration
        </h5>
        <dl className="grid grid-cols-1 sm:grid-cols-3 m-0">
          <Fact term="Service Model">{serviceModel}</Fact>
          <Fact term="Dumpster">
            {containers.length > 0 ? (
              containers.map((c, ind) => (
                <span key={ind} className="block">
                  {c.name}
                  <span className="block text-xs font-normal text-text-disabled">
                    Quantity {c.quantity}
                  </span>
                </span>
              ))
            ) : (
              <span className="text-muted">No dumpster</span>
            )}
          </Fact>
          <Fact term="Material">
            {materialNames.length > 0 ? (
              materialNames.join(', ')
            ) : (
              <span className="text-muted">No materials selected</span>
            )}
          </Fact>
        </dl>
      </section>

      <section
        aria-labelledby="job-summary-add-ons"
        className="w-full rounded-lg border border-disabled p-4"
      >
        <div className="mb-4 flex flex-row flex-wrap items-center justify-between gap-2">
          <h5 id="job-summary-add-ons" className="min-w-0 text-[13px] font-semibold text-text-light">
            Add-ons
          </h5>
          <Badge variant="secondary" aria-label={`${addOnCount} add-ons and products`}>
            {addOnCount}
          </Badge>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2">
          <section
            aria-labelledby="job-summary-existing-add-ons"
            className="min-w-0 pb-4 sm:pb-0 sm:pr-5"
          >
            <h6
              id="job-summary-existing-add-ons"
              className="mb-3 text-xs font-semibold text-text-light"
            >
              Existing Add-ons / Products
            </h6>
            {atBooking.length > 0 ? (
              <ul className="flex flex-col gap-2.5 list-disc pl-4 marker:text-text-disabled">
                {atBooking.map((addOn, ind) => (
                  <li
                    key={`${addOn._id}-${ind}`}
                    className="text-sm text-text-primary break-words"
                  >
                    {addOn.title}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted">No existing add-ons or products</p>
            )}
          </section>

          {/* Post-booking additions move the order total, so the group keeps the amber
              attention treatment used for them elsewhere on the page. The label and the
              per-item metadata carry the meaning - never color alone. */}
          <section
            aria-labelledby="job-summary-new-add-ons"
            className="min-w-0 pt-4 border-t border-disabled sm:pt-0 sm:pl-5 sm:border-t-0 sm:border-l"
          >
            <h6
              id="job-summary-new-add-ons"
              className={`mb-3 text-xs font-semibold ${ADDED_AFTER_BOOKING_TEXT}`}
            >
              New Add-ons / Products
            </h6>
            {addedAfterBooking.length > 0 ? (
              <ul className="flex flex-col gap-2.5 list-disc pl-4 marker:text-icon-warning">
                {addedAfterBooking.map((addOn, ind) => (
                  <li key={`${addOn._id}-${ind}`} className="text-sm text-text-primary break-words">
                    {addOn.title}
                    {(addOn.addedAt || addOn.addedByUserId) && (
                      <span className="block text-xs text-muted">
                        {formatActorLine(addOn.addedAt, addOn.addedByUserId)}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted">No new add-ons or products</p>
            )}
          </section>
        </div>
      </section>
    </div>
  );
};
